import React, {useState, useMemo, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {sendMagicLink} from 'externalAPIs/insurance-eligibility';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import styles from './ExpiredLinkScreen.module.css';

/**
 * Extracts email from URL search params, preserving + signs.
 * URLSearchParams.get() converts + to space, so we manually decode the email parameter.
 */
const extractEmailFromUrl = (searchParams: URLSearchParams): string | null => {
  if (typeof window === 'undefined') {
    return searchParams.get('email');
  }

  // Get raw email parameter from URL search string to preserve + signs
  const searchString = window.location.search;
  const emailMatch = searchString.match(/[?&]email=([^&]*)/);
  if (emailMatch) {
    // Replace + with %2B before decoding to preserve + signs in email addresses
    return decodeURIComponent(emailMatch[1].replace(/\+/g, '%2B'));
  }

  return searchParams.get('email');
};

export const ExpiredLinkScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailFromQuery = useMemo(() => extractEmailFromUrl(searchParams), [searchParams]);

  // Get stored email from survey data
  const storedEmail = useMemo(() => {
    const surveyData = SurveyData.read() ?? {};
    return surveyData.magic_link_email ?? null;
  }, []);

  // Prefer query param email, fallback to stored email
  const initialEmail = emailFromQuery ?? storedEmail ?? '';

  const [email, setEmail] = useState<string>(initialEmail);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [requestStatus, setRequestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Sync email state with query param changes
  useEffect(() => {
    if (emailFromQuery) {
      setEmail(emailFromQuery);
    }
  }, [emailFromQuery]);

  const hasPreFilledEmail = Boolean(initialEmail);

  const handleRequestNewLink = async () => {
    if (!email || !email.trim()) {
      setErrorMessage('Please enter your email address');
      setRequestStatus('error');
      return;
    }

    setIsRequesting(true);
    setRequestStatus('idle');
    setErrorMessage('');

    try {
      const response = await sendMagicLink(email.trim());
      if (response.error) {
        setErrorMessage(response.error);
        setRequestStatus('error');
      } else {
        setRequestStatus('success');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to send magic link';
      setErrorMessage(errorMsg);
      setRequestStatus('error');
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>Link Expired</h1>
        <p className={styles.description}>
          Your access link has expired. Please request a new link to continue managing your appointments.
        </p>

        {requestStatus === 'success' ? (
          <div className={styles.successMessage}>
            <p>✓ Magic link sent! Please check your email and click the link to continue.</p>
          </div>
        ) : (
          <>
            <div className={styles.emailInputContainer}>
              <label htmlFor="email-input" className={styles.label}>
                Email Address
              </label>
              <input
                id="email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className={styles.emailInput}
                disabled={isRequesting}
              />
              {hasPreFilledEmail ? (
                <p className={styles.helperText}>You can enter a different email address if needed</p>
              ) : null}
            </div>

            {errorMessage && (
              <div className={styles.errorMessage}>
                <p>{errorMessage}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handleRequestNewLink}
              disabled={isRequesting || !email.trim()}
              className={styles.requestButton}
            >
              {isRequesting ? 'Sending...' : 'Request New Link'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
